import React, {useCallback, useMemo, useState} from 'react';

import {InstrumentedWaveLoader} from '../../../utility/InstrumentedLoader';
import {SettingLabel} from '../components/SettingLabel';
import {
  type BucketCustomization,
  type SemanticLegendSettingsWithCategoricalConfig,
} from '../types';
import {CategoryCustomizationInput} from './CategoryCustomizationInput';
import {SemanticLegendBucketsActionsWithCategoricalConfig} from './SemanticLegendBucketsActionsWithCategoricalConfig';
import {SemanticLegendsPaletteWithCategoricalConfig} from './SemanticLegendsPaletteWithCategoricalConfig';
import {type SemanticLegendPalette} from './utils/colorPalettes';

// Static empty object to prevent re-renders
const EMPTY_CATEGORY_CUSTOMIZATION: BucketCustomization = {};

export type SemanticLegendCategoriesProps = {
  selectedCategories: string[];
  categoryCustomizations: Record<number, BucketCustomization>;
  allCategories: string[];
  semanticLegendPalette: SemanticLegendPalette;
  updateSetting: (
    settings: Partial<SemanticLegendSettingsWithCategoricalConfig>
  ) => void;
  isLoadingBuckets: boolean;
};

export const SemanticLegendCategories = ({
  selectedCategories,
  categoryCustomizations,
  allCategories,
  semanticLegendPalette,
  updateSetting,
  isLoadingBuckets,
}: SemanticLegendCategoriesProps) => {
  const updateCategoryCustomizationSetting = useCallback(
    (settings: Partial<BucketCustomization>, index: number) => {
      const newCategoryCustomizations = {...categoryCustomizations};
      newCategoryCustomizations[index] = {
        ...newCategoryCustomizations[index],
        ...settings,
      };
      updateSetting({
        bucketCustomizations: newCategoryCustomizations,
      });
    },
    [updateSetting, categoryCustomizations]
  );

  const updateCategoryValue = useCallback(
    (selected: {value: string; label: string} | null, index: number) => {
      if (selected) {
        const selectedCategoryExistingIndex = selectedCategories.indexOf(
          selected.value
        );
        const originalCategory = selectedCategories[index];
        const newCategories = [...selectedCategories];
        newCategories[index] = selected.value;
        // If the selected category already exists, swap the original category with the selected category
        if (selectedCategoryExistingIndex !== -1) {
          newCategories[selectedCategoryExistingIndex] = originalCategory;
        }

        updateSetting({selectedCategories: newCategories});
      }
    },
    [updateSetting, selectedCategories]
  );

  // Create stable callback handlers - this is the key performance fix
  const categoryUpdateHandlers = useMemo(() => {
    return selectedCategories.map((_, index) => ({
      updateCategorySetting: (settings: Partial<BucketCustomization>) =>
        updateCategoryCustomizationSetting(settings, index),
      updateCategoryValue: (selected: {value: string; label: string} | null) =>
        updateCategoryValue(selected, index),
    }));
  }, [
    selectedCategories,
    updateCategoryCustomizationSetting,
    updateCategoryValue,
  ]);

  // Memoize the category list to prevent re-creation on every render
  const categoryCustomizationList = useMemo(() => {
    return selectedCategories?.map((category, index) => {
      // Use static empty object instead of creating new one each time
      const categoryCustomization =
        categoryCustomizations[index] ?? EMPTY_CATEGORY_CUSTOMIZATION;

      return (
        // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
        <li key={`category-${index}-${category}`}>
          <CategoryCustomizationInput
            category={category}
            index={index}
            categoryCustomization={categoryCustomization}
            categoriesList={allCategories}
            numberOfCurrentCategories={selectedCategories.length}
            semanticLegendPalette={semanticLegendPalette}
            updateCategorySetting={
              categoryUpdateHandlers[index].updateCategorySetting
            }
            updateCategoryValue={
              categoryUpdateHandlers[index].updateCategoryValue
            }
          />
        </li>
      );
    });
  }, [
    selectedCategories,
    allCategories,
    categoryCustomizations,
    semanticLegendPalette,
    categoryUpdateHandlers,
  ]);

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col gap-8" data-test="categories-section">
      <div className="flex w-full gap-8">
        <div className="flex w-full gap-8">
          <SettingLabel text="Categories" className="w-fit" />
          {isLoadingBuckets && (
            <InstrumentedWaveLoader name="buckets-loading" size="small" />
          )}
        </div>
        <SemanticLegendsPaletteWithCategoricalConfig />
        <SemanticLegendBucketsActionsWithCategoricalConfig
          setIsOpen={setIsOpen}
          isOpen={isOpen}
        />
      </div>
      <ul className="flex flex-col gap-8">{categoryCustomizationList}</ul>
    </div>
  );
};
